import { useAppStore } from '../store/appStore'
import {
    clearInteractiveSignIn,
    tryMarkInteractiveSignIn,
    useInteractiveSignInPending,
} from '../store/interactiveSignIn'

/**
 * Guards one interactive sign-in attempt (a Google/Microsoft popup, a password submit) at a
 * time — across both entry points, not just within one. Firebase's client SDK only ever tracks
 * one signed-in user per tab, so if a Google popup were left pending while the user separately
 * completed a password sign-in, whichever one resolved last would silently take over as the
 * signed-in account; blocking a second attempt anywhere while one is pending (via the shared
 * useInteractiveSignInPending store) prevents that race outright instead of reconciling it after
 * the fact.
 *
 * isPending also covers the session-resolving phase that follows a successful attempt (the /me
 * fetch), so the button stays disabled until the session actually settles, not just until the
 * token appears — and because it's derived, not a manually-toggled flag, it can't get left stuck
 * on by a code path that forgets to reset it.
 */
export const useSignInAttemptGuard = () => {
    const isAttemptPending = useInteractiveSignInPending()
    const sessionTag = useAppStore((state) => state.session.tag)
    const isPending = isAttemptPending || sessionTag === 'resolving'

    // Reads live store state here, not this hook instance's own sessionTag closure above — that
    // value is from this instance's last render, and can still read a stale tag (e.g.
    // 'unauthenticated') for a window after another attempt already moved the session to
    // 'resolving' but before this instance has rerendered to reflect it. Same reasoning as
    // tryMarkInteractiveSignIn's own atomic claim, and same fix: check the store directly at the
    // moment of the call. Allow-listed against the two tags sessionActions.tokenAppeared itself
    // accepts as a starting point, rather than excluding just 'resolving' — an exclusion list
    // defaults to *allowing* a tag nobody has reasoned about yet, the wrong default here too.
    const start = (): number | null => {
        const currentSessionTag = useAppStore.getState().session.tag
        if (currentSessionTag !== 'unauthenticated' && currentSessionTag !== 'accountNotFound') return null
        return tryMarkInteractiveSignIn()
    }

    return { isPending, start, cancel: clearInteractiveSignIn }
}
